import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { resolveServerUrl } from './serverUrl';
import { LocalNotifications } from '@capacitor/local-notifications';
import { getActiveRoomId } from './activeChat';

let isInitialized = false;

export const initPushNotifications = async (onMessageReceived) => {
  // Only run on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('🔔 Push notifications: Not on native platform, skipping');
    return null;
  }

  // Guard immediately to avoid StrictMode double-invoke registering twice
  if (isInitialized) {
    console.log('🔔 Push notifications already initialized');
    return;
  }
  isInitialized = true;

  try {
    console.log('🔔 Initializing push notifications...');

    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    console.log('🔔 Permission status:', permStatus);

    if (permStatus.receive === 'granted') {
      // Register with APNs / FCM
      await PushNotifications.register();
      console.log('🔔 Registered for push notifications');

      // Listen for registration success
      await PushNotifications.addListener('registration', (token) => {
        // Return token so we can send it to backend
        if (onMessageReceived) {
          onMessageReceived({ type: 'registration', token: token.value });
        }
      });

      // Listen for registration errors
      await PushNotifications.addListener('registrationError', (error) => {});

      // Listen for push notifications received while app is in foreground
      await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
        if (onMessageReceived) {
          onMessageReceived({ type: 'foreground', notification });
        }

        // Stop local notifications while the relevant chat is open
        try {
          const roomId = notification?.data?.roomId || notification?.notification?.data?.roomId;
          const active = getActiveRoomId();
          if (roomId && active && roomId === active) {
            return;
          }
        } catch (_) {}

        // Show local notification
        await LocalNotifications.schedule({
          notifications: [{
            title: notification.title || 'New Message',
            body: notification.body || 'You have a new message',
            id: Math.floor(Math.random() * 1000000),
            sound: 'default',
            channelId: 'tchat_messages'
          }]
        });
      });

      // Listen for push notification tapped (app opened from notification)
      await PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
        console.log('🔔 Push notification action performed:', notification);
        if (onMessageReceived) {
          onMessageReceived({ type: 'action', notification });
        }

        // Navigate to the chat if roomId is available
        const roomId = notification?.notification?.data?.roomId;
        if (roomId) {
          // Update URL to trigger chat opening
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('openChat', roomId);
          window.history.pushState({}, '', currentUrl.toString());
          
          // Trigger a storage event to notify the app
          window.dispatchEvent(new Event('popstate'));
        }
      });

      console.log('🔔 Push notifications initialized successfully');
    } else {
      console.log('🔔 Push notification permission denied');
    }
  } catch (error) {
    console.error('🔔 Error initializing push notifications:', error);
  }
};

export const registerFCMToken = async (token, userToken) => {
  try {
    const serverUrl = resolveServerUrl();

    console.log('🔔 Registering FCM token with server:', serverUrl);
    
    const response = await fetch(`${serverUrl}/api/fcm/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userToken}`
      },
      body: JSON.stringify({ token })
    });

    if (response.ok) {
      console.log('🔔 FCM token registered successfully');
    } else {
      console.error('🔔 Failed to register FCM token:', response.status);
    }
  } catch (error) {
    console.error('🔔 Error registering FCM token:', error);
  }
};

